import { useContext, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useList, useNotification } from '@refinedev/core';
import BaseService from '../BaseService';
import { CommonContentContext } from '@/utils/context/commonContent/CommonContentContext';
import { TenantDetailsDto } from '@/utils/company/dtos/TenantDetailsDto';
import { captureAppException, getHttpErrorContext } from '@/utils/sentry/sentryLogger';

const TenantAuthWrapper = ({ children }) => {
  const router = useRouter();

  const { setRecordMachinesAndImplements, setTenantId } = useContext(CommonContentContext);

  const { open } = useNotification();

  const { data: tenantsData, isLoading } = useList({
    resource: 'Tenants',
    errorNotification: (res) => {
      captureAppException(res, {
        flow: 'app-startup',
        resource: 'Tenants',
        source: 'TenantAuthWrapper',
        ...getHttpErrorContext(res),
      });
      const statusCode = 401;
      if (res?.response?.status === statusCode) {
        return { description: 'Failure', message: 'Unauthorized user.', type: 'error' };
      }
      return { description: 'Failure', message: 'Could not load tenants', type: 'error' };
    },
  });

  const shouldHaveTenant = (accessToken: string | null, isAdmin: boolean, hasTenant: string | null) =>
    isAdmin && !hasTenant && accessToken;

  useEffect(() => {
    const accessToken = localStorage.getItem('ACCESS_TOKEN');
    const tenantId = localStorage.getItem('TENANT_ID');
    let isAdmin = false;

    if (accessToken) {
      BaseService.get('/Users/AdminVerification')
        .then((response) => {
          isAdmin = response.data;
          if (!isLoading && shouldHaveTenant(accessToken, isAdmin, tenantId)) {
            router.push('/company/create');
          }
        })
        .catch((error) => {
          captureAppException(error, {
            flow: 'app-startup',
            resource: 'Users/AdminVerification',
            ...getHttpErrorContext(error),
          });
          const statusCode = 404;
          if (error.response?.status === statusCode) {
            open?.({
              description: 'Failure',
              message: 'User not found.',
              type: 'error',
            });
          } else {
            open?.({
              description: 'Failure',
              message: 'Unauthorized user.',
              type: 'error',
            });
          }
        });
      if (tenantId) {
        localStorage.setItem('TENANT_ID', String(tenantId));
        setTenantId(String(tenantId));
      }
    }
  }, [isLoading, tenantsData?.data]);

  useEffect(() => {
    const tenantId = localStorage.getItem('TENANT_ID');
    if (tenantId) {
      BaseService.get<TenantDetailsDto>('TenantDetails')
        .then((response) => {
          response.data && setRecordMachinesAndImplements(response.data.recordMachinesAndImplements);
        })
        .catch((error) => {
          captureAppException(error, {
            flow: 'app-startup',
            resource: 'TenantDetails',
            tenantId,
            ...getHttpErrorContext(error),
          });
          console.warn('Tenant Details not found.');
        });
    }
  }, []);

  return children;
};

export default TenantAuthWrapper;
